import { DeleteOutlined, EditOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Dropdown, Input, Menu, message, Typography } from 'antd';
import confirm from 'antd/lib/modal/confirm';
import { Breadcrumb, Button, Content, Space, SpacedContent, Table, Title } from 'components/lib';
import { ElementRef, useRef, useState } from 'react';
import { Medication, useDeleteMedicationMutation, useGetMedicinesQuery } from 'services/medicines.service';
import { format } from 'utils/helpers';
import { formatCurrency, roundTo } from 'utils/helpers/format';
import { sortRows } from 'utils/helpers/general';
import CreateOrUpdateMedicationModal from './CreateOrUpdateMedicationModal';
import { convertFromMicro } from './MedicationUnitSelect';

const MedicationsListing: React.FC = () => {
    const medicationModalRef = useRef<ElementRef<typeof CreateOrUpdateMedicationModal>>(null);
    const [search, setSearch] = useState('');

    const { data: medicationsList } = useGetMedicinesQuery();
    const [deleteMedication] = useDeleteMedicationMutation();

    const filteredData = medicationsList?.filter((medication) => {
        if (search === '') return medication;

        const regex = new RegExp(search.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'gi');

        return medication.name.match(regex);
    });

    const confirmDeleteMedication = (medication: Medication) => {
        confirm({
            title: 'Delete Medication',
            icon: <ExclamationCircleOutlined style={{ color: '#e7202e' }} />,
            content: <p>Are you sure you want to delete {medication.name}?</p>,
            okText: 'Confirm',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk() {
                deleteMedication(medication.id).then((res) => {
                    if ('error' in res) {
                        message.error(`There was an error deleting ${medication.name}`);
                        return;
                    }

                    message.success(`${medication.name} successfully deleted`);
                });
            },
        });
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: sortRows('name'),
            defaultSortOrder: 'ascend',
            width: 350,
            render: (name: string) => format.capitalizeStr(name),
        },
        {
            title: 'Brand',
            dataIndex: 'brand_name',
            key: 'brand_name',
            sorter: sortRows('brand_name'),
            ellipsis: true,
        },
        {
            title: 'Form',
            dataIndex: 'form',
            key: 'form',
            sorter: sortRows('form'),
            ellipsis: true,
            render: (form: string) => format.capitalizeStr(form),
        },
        {
            title: 'Default Dose',
            dataIndex: 'default_dose',
            key: 'default_dose',
            ellipsis: true,
            render: (dose: number, medication: Medication) =>
                dose ? `${Math.round(dose) / 1000} ${convertFromMicro(medication.default_dose_unit)}` : '-',
        },
        {
            title: 'Controlled Drug',
            dataIndex: 'controlled_drug',
            key: 'controlled_drug',
            render: (controlled_drug: boolean) => (controlled_drug ? 'Yes' : 'No'),
        },
        {
            title: 'Base Unit Price',
            dataIndex: 'base_price_cents',
            key: 'base_price_cents',
            sorter: sortRows('base_price_cents'),
            render: (basePrice: number) => formatCurrency(roundTo(basePrice / 100, 2)),
        },
        {
            title: 'Sort Rank',
            dataIndex: 'sort_rank',
            key: 'sort_rank',
            width: 120,
            sorter: sortRows('sort_rank'),
        },
        {
            title: 'Product Category',
            dataIndex: 'product_category_name',
            key: 'product_category_name',
            sorter: sortRows('product_category_name'),
        },
        {
            title: '',
            key: 'action',
            dataIndex: 'id',
            width: 100,
            render: (id: string, medication: Medication) => {
                return (
                    <Dropdown
                        key={id}
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key='edit-medication'
                                    onClick={() => {
                                        medicationModalRef.current?.setMedicationToUpdate(medication);
                                    }}
                                >
                                    <SpacedContent>
                                        Edit <EditOutlined />
                                    </SpacedContent>
                                </Menu.Item>
                                <Menu.Item
                                    key='delete-medication'
                                    onClick={() => {
                                        confirmDeleteMedication(medication);
                                    }}
                                >
                                    <SpacedContent>
                                        Remove <DeleteOutlined />
                                    </SpacedContent>
                                </Menu.Item>
                            </Menu>
                        }
                        placement='bottomRight'
                        arrow
                    >
                        <Button>
                            <EllipsisOutlined />
                        </Button>
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <>
            <Breadcrumb path={['Medications', 'Dashboard']} />
            <Content>
                <Title level={2}>Medications</Title>
                <Space direction='vertical' full>
                    <Typography.Text>{medicationsList?.length} items</Typography.Text>
                    <SpacedContent>
                        <Space>
                            <Input
                                placeholder='Search Medication'
                                suffix={<SearchOutlined />}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Space>
                        <Button
                            onClick={() => {
                                medicationModalRef.current?.openModal();
                            }}
                        >
                            New Medication <PlusOutlined />
                        </Button>
                    </SpacedContent>
                    <Table columns={columns} data={filteredData ?? []} />
                </Space>
                <CreateOrUpdateMedicationModal ref={medicationModalRef} />
            </Content>
        </>
    );
};

export default MedicationsListing;
